import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, ChevronLeft, ChevronRight, CheckCircle, PenLine, Lock, Eye, Printer, X } from 'lucide-react';

const API_URL = '';

export default function InteractiveNoteViewer({ note, studentId, onClose }) {
  const [currentSection, setCurrentSection] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [studentAnswers, setStudentAnswers] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [previousAnswers, setPreviousAnswers] = useState(null);
  const [loading, setLoading] = useState(true);

  const aiContent = typeof note.content === 'string' ? JSON.parse(note.content) : (note.content || {});
  const aiImages = typeof note.images === 'string' ? JSON.parse(note.images) : (note.images || {});
  const sections = aiContent.sections || [];
  const questions = aiContent.practice_questions || [];
  const totalSteps = sections.length + (questions.length > 0 ? 1 : 0);

  // Check if student already answered
  const checkPreviousAnswers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/student-portal/notes/${note.id}/my-answers?student_id=${studentId}`);
      if (res.ok) {
        const data = await res.json();
        if (data.submitted) {
          setPreviousAnswers(data.answers);
          setSubmitted(true);
          // Restore answers
          const restored = {};
          data.answers.forEach(a => { restored[a.question_index] = a.student_answer; });
          setStudentAnswers(restored);
        }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [note.id, studentId]);

  useEffect(() => { checkPreviousAnswers(); }, [checkPreviousAnswers]);

  const handleAnswerChange = (qIndex, value) => {
    setStudentAnswers(prev => ({ ...prev, [qIndex]: value }));
  };

  const allAnswered = questions.every((_, i) => (studentAnswers[i] || '').trim().length > 0);

  const handleSubmit = async () => {
    if (!allAnswered) return;
    const answers = questions.map((q, i) => ({
      question_index: i,
      question: q.question,
      student_answer: (studentAnswers[i] || '').trim(),
      correct_answer: q.answer,
    }));
    setSubmitError('');
    try {
      const res = await fetch(`${API_URL}/api/student-portal/notes/${note.id}/submit-answers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_id: studentId, answers }),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      setPreviousAnswers(answers);
      setSubmitted(true);
    } catch (e) {
      console.error(e);
      setSubmitError('Your answers could not be sent to your teacher. Check your connection and try again.');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-xl p-8 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-3"></div>
          <p className="text-sm text-gray-500">Loading notes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 print:p-0 print:bg-white" data-testid="interactive-note-viewer">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden print:max-w-full print:max-h-full print:shadow-none print:rounded-none">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-700 to-teal-600 px-6 py-4 flex items-center justify-between print:bg-emerald-700">
          <div>
            <h2 className="text-lg font-bold text-white">{aiContent.title || note.title}</h2>
            <p className="text-emerald-100 text-sm">{note.subject} — Year {note.year_level}</p>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <button onClick={handlePrint} className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors" title="Print">
              <Printer className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors" data-testid="close-viewer">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="px-6 py-2 bg-gray-50 border-b print:hidden">
          <div className="flex items-center gap-2">
            {sections.map((_, i) => (
              <button key={i} onClick={() => { setCurrentSection(i); setShowQuiz(false); }}
                className={`flex-1 h-2 rounded-full transition-all ${
                  i < currentSection || showQuiz ? 'bg-emerald-500' :
                  i === currentSection && !showQuiz ? 'bg-emerald-400 animate-pulse' : 'bg-gray-200'
                }`} data-testid={`progress-${i}`} />
            ))}
            {questions.length > 0 && (
              <button onClick={() => { if (currentSection >= sections.length - 1 || submitted) setShowQuiz(true); }}
                className={`flex-1 h-2 rounded-full transition-all ${
                  showQuiz ? (submitted ? 'bg-emerald-500' : 'bg-amber-400 animate-pulse') : 'bg-gray-200'
                }`} data-testid="progress-quiz" />
            )}
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-500">
              {showQuiz ? 'Practice Questions' : `Section ${currentSection + 1} of ${sections.length}`}
            </span>
            {questions.length > 0 && !submitted && !showQuiz && (
              <span className="text-xs text-amber-600 flex items-center gap-1">
                <Lock className="w-3 h-3" /> Complete quiz to finish
              </span>
            )}
            {submitted && (
              <span className="text-xs text-emerald-600 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Quiz completed
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5" data-testid="note-content">
          {!showQuiz ? (
            /* Section View */
            sections[currentSection] && (
              <div data-testid={`section-${currentSection}`}>
                <h3 className="text-xl font-bold text-emerald-800 mb-3 pb-2 border-b-2 border-emerald-100">
                  {sections[currentSection].heading}
                </h3>

                <div className="text-gray-700 leading-relaxed whitespace-pre-line text-sm mb-4">
                  {sections[currentSection].content}
                </div>

                {/* Key Terms */}
                {sections[currentSection].key_terms?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {sections[currentSection].key_terms.map((term, i) => (
                      <span key={i} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold border border-blue-100">
                        {term}
                      </span>
                    ))}
                  </div>
                )}

                {/* Image */}
                {aiImages[String(currentSection)] && (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4 text-center">
                    <img
                      src={`data:image/png;base64,${aiImages[String(currentSection)]}`}
                      alt={sections[currentSection].image_description || 'Diagram'}
                      className="max-w-full max-h-[350px] mx-auto rounded-lg"
                    />
                    {sections[currentSection].image_description && (
                      <p className="text-xs text-gray-500 italic mt-2">{sections[currentSection].image_description}</p>
                    )}
                  </div>
                )}
              </div>
            )
          ) : (
            /* Quiz View */
            <div data-testid="quiz-section">
              <div className="flex items-center gap-2 mb-5">
                <PenLine className="w-5 h-5 text-amber-600" />
                <h3 className="text-xl font-bold text-gray-900">Practice Questions</h3>
              </div>
              <p className="text-sm text-gray-500 mb-6 bg-amber-50 border border-amber-200 rounded-lg p-3">
                {submitted
                  ? 'You have completed the practice questions. Your answers and the correct answers are shown below.'
                  : 'Answer ALL questions below before you can see the correct answers. Take your time and try your best!'}
              </p>

              <div className="space-y-6">
                {questions.map((q, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm" data-testid={`question-${i}`}>
                    <p className="font-semibold text-gray-900 text-sm mb-2">
                      <span className="text-emerald-600 font-bold mr-1">Q{i + 1}.</span>
                      {q.question}
                    </p>

                    {!submitted ? (
                      <textarea
                        value={studentAnswers[i] || ''}
                        onChange={e => handleAnswerChange(i, e.target.value)}
                        placeholder="Type your answer here..."
                        rows={3}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                        data-testid={`answer-input-${i}`}
                      />
                    ) : (
                      <div className="space-y-3">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-xs font-semibold text-blue-700 mb-1 flex items-center gap-1">
                            <PenLine className="w-3 h-3" /> Your Answer:
                          </p>
                          <p className="text-sm text-blue-900">{studentAnswers[i] || '(No answer provided)'}</p>
                        </div>
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                          <p className="text-xs font-semibold text-emerald-700 mb-1 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Correct Answer:
                          </p>
                          <p className="text-sm text-emerald-900">{q.answer}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {!submitted && (
                <div className="mt-6 text-center">
                  {submitError && <p className="mb-3 text-sm text-red-600 font-medium" data-testid="submit-answers-error">{submitError}</p>}
                  <button
                    onClick={handleSubmit}
                    disabled={!allAnswered}
                    className={`inline-flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-bold shadow-lg transition-all ${
                      allAnswered
                        ? 'bg-emerald-600 text-white hover:bg-emerald-700 hover:shadow-xl'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                    data-testid="submit-answers-btn"
                  >
                    <Eye className="w-4 h-4" />
                    {allAnswered ? 'Submit & Reveal Answers' : `Answer all ${questions.length} questions first`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="px-6 py-3 bg-gray-50 border-t flex items-center justify-between print:hidden">
          <button
            onClick={() => {
              if (showQuiz) { setShowQuiz(false); setCurrentSection(sections.length - 1); }
              else if (currentSection > 0) setCurrentSection(currentSection - 1);
            }}
            disabled={currentSection === 0 && !showQuiz}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="prev-btn"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>

          <span className="text-xs text-gray-400">
            {showQuiz ? `Practice Questions` : `${currentSection + 1} / ${sections.length}`}
          </span>

          <button
            onClick={() => {
              if (showQuiz) return;
              if (currentSection < sections.length - 1) {
                setCurrentSection(currentSection + 1);
              } else if (questions.length > 0) {
                setShowQuiz(true);
              }
            }}
            disabled={showQuiz || (currentSection === sections.length - 1 && questions.length === 0)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              currentSection === sections.length - 1 && questions.length > 0 && !showQuiz
                ? 'bg-amber-500 text-white hover:bg-amber-600'
                : 'bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-30 disabled:cursor-not-allowed'
            }`}
            data-testid="next-btn"
          >
            {currentSection === sections.length - 1 && questions.length > 0 && !showQuiz
              ? <>Practice Questions <PenLine className="w-4 h-4" /></>
              : <>Next <ChevronRight className="w-4 h-4" /></>
            }
          </button>
        </div>
      </div>
    </div>
  );
}
